import { CurrencyCodes } from 'common/configs/currency';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import {
  userAccountsWithBalanceRecipe,
  xratesRecipe,
} from 'common/state/accounts/recipes';
import {
  defaultUserAccountsWithBalanceState,
  defaultXratesState,
} from 'common/state/accounts/states';
import {
  doGetUserAccountsWithBalance,
  doGetXrates,
} from 'common/state/accounts/thunkActions';
import { userWalletsWithBalanceRecipe } from 'common/state/banking/recipes';
import { defaultWalletsWithBalanceState } from 'common/state/banking/states';
import { doGetUserWalletsWithBalance } from 'common/state/banking/thunkActions';
import { ClientTransfer } from 'common/state/banking/transfers/actions';
import {
  incomingUserTransfersRecipe,
  outgoingUserTransfersRecipe,
} from 'common/state/banking/transfers/recipes';
import { defaultClientTransfersState } from 'common/state/banking/transfers/states';
import {
  doGetUserIncomingTransfers,
  doGetUserOutgoingTransfers,
} from 'common/state/banking/transfers/thunkActions';
import { Bet } from 'common/state/bets/actions';
import { requestBets } from 'common/state/bets/thunkActions';
import {
  customerRecipe,
  customerSummariesRecipe,
} from 'common/state/customers/recipes';
import {
  defaultCustomerState,
  defaultCustomerSummariesState,
} from 'common/state/customers/states';
import {
  doGetCustomer,
  doGetAllCustomerSummaries,
} from 'common/state/customers/thunkActions';
import { PaginatedResponse } from 'common/state/types';
import { TransfersTable } from './components/transfersTable';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { AccountSummaryTable } from './components/accountSummaryTable';
import { CustomerInfoTable } from './components/customerInfoTable';
import { StatementsTable } from './components/statementsTable';
import { WalletsTable } from './components/walletsTable';
import { CreditModal } from './components/creditModal';
import { ThresholdModal } from './components/thresholdModal';
import { MinBalanceModal } from './components/minBalanceModal';
import { ThemedModal } from 'common/components/cardStyles';
import FadeIn from 'react-fade-in';
import { Wallet } from 'common/state/banking/actions';
import { XactAccountsTable } from './components/xactAccountsTable';
import { AccountsTable } from './components/accountsTable';
import { AdjustmentsTable } from './components/adjustmentsTable';
import { AdjustmentModal } from './components/adjustmentModal';
import { Button } from '@mollybet/ui';
import { api } from 'api';
import { doGetProviderSummaries } from 'common/state/provider/thunkActions';
import { providerSummariesRecipe } from 'common/state/provider/recipes';
import { defaultProviderSummariesState } from 'common/state/provider/states';

export interface CustomerParams {
  username: string;
}

export interface AccountBetResponseMap {
  [username: string]: PaginatedResponse<Bet>;
}

export type TransferCurrencyMap = Partial<{
  [currency in CurrencyCodes]: ClientTransfer[];
}>;

export type ModalModes =
  | 'credit'
  | 'threshold'
  | 'minBalance'
  | 'adjustment'
  | null;

const Container = styled.div`
  flex: 1;
  margin: 1rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: calc(100vw - 2rem);
`;

const SudoButton = styled(Button)`
  margin-left: 3rem;
`;

export const Customer = () => {
  const { username } = useParams<CustomerParams>();
  const [modalMode, setModalMode] = useState<ModalModes>();
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<Wallet>();
  const [customerState, customerDispatch] = useProducerWithThunks(
    customerRecipe,
    defaultCustomerState
  );
  const [providerSummariesState, providerSummariesDispatch] =
    useProducerWithThunks(
      providerSummariesRecipe,
      defaultProviderSummariesState
    );
  const [customerSummariesState, customerSummariesDispatch] =
    useProducerWithThunks(
      customerSummariesRecipe,
      defaultCustomerSummariesState
    );
  const [walletsState, walletsDispatch] = useProducerWithThunks(
    userWalletsWithBalanceRecipe,
    defaultWalletsWithBalanceState
  );
  const [xratesState, xratesDispatch] = useProducerWithThunks(
    xratesRecipe,
    defaultXratesState
  );
  const [accountsState, accountsDispatch] = useProducerWithThunks(
    userAccountsWithBalanceRecipe,
    defaultUserAccountsWithBalanceState
  );
  const [accountBets, setAccountBets] = useState<AccountBetResponseMap | null>(
    null
  );
  const [incomingTransferState, incomingTransferDispatch] =
    useProducerWithThunks(
      incomingUserTransfersRecipe,
      defaultClientTransfersState
    );
  const [outgoingTransferState, outgoingTransferDispatch] =
    useProducerWithThunks(
      outgoingUserTransfersRecipe,
      defaultClientTransfersState
    );
  const [clientCode, setClientCode] = useState<string>();

  useEffect(() => {
    customerSummariesDispatch(doGetAllCustomerSummaries());
    providerSummariesDispatch(doGetProviderSummaries({}));
    xratesDispatch(doGetXrates());
  }, [customerSummariesDispatch, providerSummariesDispatch, xratesDispatch]);

  useEffect(() => {
    customerDispatch(doGetCustomer(username));
    walletsDispatch(doGetUserWalletsWithBalance(username));
    accountsDispatch(doGetUserAccountsWithBalance(username));
    incomingTransferDispatch(
      doGetUserIncomingTransfers(username, { limit: 500 })
    );
    outgoingTransferDispatch(
      doGetUserOutgoingTransfers(username, { limit: 500 })
    );
  }, [
    customerDispatch,
    walletsDispatch,
    accountsDispatch,
    incomingTransferDispatch,
    outgoingTransferDispatch,
    username,
  ]);

  useEffect(() => {
    if (shouldRefresh) {
      customerDispatch(doGetCustomer(username));
      customerSummariesDispatch(doGetAllCustomerSummaries());
      providerSummariesDispatch(doGetProviderSummaries({}));
      walletsDispatch(doGetUserWalletsWithBalance(username));
      accountsDispatch(doGetUserAccountsWithBalance(username));
      xratesDispatch(doGetXrates());
      incomingTransferDispatch(
        doGetUserIncomingTransfers(username, { limit: 500 })
      );
      outgoingTransferDispatch(
        doGetUserOutgoingTransfers(username, { limit: 500 })
      );
      setShouldRefresh(false);
    }
  }, [
    customerDispatch,
    customerSummariesDispatch,
    providerSummariesDispatch,
    walletsDispatch,
    accountsDispatch,
    xratesDispatch,
    incomingTransferDispatch,
    outgoingTransferDispatch,
    username,
    shouldRefresh,
  ]);

  useEffect(() => {
    if (accountsState?.accounts?.length) {
      const usernamesToFetch = [];
      accountsState.accounts.forEach((account) => {
        if (!accountBets || !(account.username in accountBets)) {
          usernamesToFetch.push(account.username);
        }
      });
      if (usernamesToFetch.length)
        Promise.all(
          usernamesToFetch.map((username) =>
            requestBets({ username }).then((response) => response.data)
          )
        ).then((bets) => {
          setAccountBets(
            bets.reduce(
              (acc, accountBets, index) => ({
                ...acc,
                [usernamesToFetch[index]]: accountBets,
              }),
              { ...(accountBets || {}) }
            )
          );
        });
    }
    // Would cause loop
    // eslint-disable-next-line
  }, [accountsState.accounts]);

  const closeModal = () => setModalMode(null);

  const sudo = () => {
    api
      .post(`/contact/${username}/switch/`)
      .then(() => (window.location.href = '/'));
  };

  const { bets: combinedBets } = useMemo(() => {
    let count = 0;
    let bets: Bet[] | null = null;

    if (accountBets) {
      Object.values(accountBets).forEach((_accountBets) => {
        count += _accountBets.count;
        bets = [...(bets || []), ..._accountBets.results];
      });

      bets = bets
        .sort(
          (betA: Bet, betB: Bet) =>
            +new Date(betB.placed_at) - +new Date(betA.placed_at)
        )
        .slice(0, 25);
    }

    return { count, bets };
  }, [accountBets]);

  const mappedTransfers = useMemo((): TransferCurrencyMap => {
    const allTransfers = [
      ...(outgoingTransferState.transfers || []),
      ...(incomingTransferState.transfers || []).map((transfer) => ({
        ...transfer,
        amount: `-${transfer.amount}`,
        got_amount: transfer.got_amount && `-${transfer.got_amount}`,
      })),
    ]
      .sort((transferA, transferB) => {
        if (transferA.date > transferB.date) return -1;
        if (transferA.date < transferB.date) return 1;
        return 0;
      })
      .slice(0, 500);

    const transfers: TransferCurrencyMap = {};

    allTransfers.forEach((transfer) => {
      transfers[transfer.currency] = transfers[transfer.currency] || [];
      transfers[transfer.currency].push(transfer);
    });

    return transfers;
  }, [incomingTransferState.transfers, outgoingTransferState.transfers]);

  const customerSummaries = useMemo(
    () =>
      customerSummariesState.summaries?.filter(
        (summary) => summary.customer_username === username
      ),
    [username, customerSummariesState.summaries]
  );

  return (
    <Container>
      <HeaderContainer>
        <h1>
          {username}&nbsp;{customerState.customer && '-'}{' '}
          {customerState?.customer?.first_name}{' '}
          {customerState?.customer?.last_name}
        </h1>
        <SudoButton onClick={sudo}>Sudo {username}</SudoButton>
      </HeaderContainer>
      <CustomerInfoTable
        customer={customerState.customer}
        summaries={customerSummaries}
        wallets={walletsState.wallets}
        isLoading={customerState.pending}
        error={walletsState.error || customerState.error}
        bets={combinedBets}
      />
      <StatementsTable
        summaries={customerSummaries}
        xrates={xratesState.xrates}
        setClientCode={setClientCode}
        setModalMode={setModalMode}
        isLoading={customerState.pending || customerSummariesState.pending}
      />
      <AccountSummaryTable
        accounts={accountsState.accounts}
        bets={combinedBets}
        customer={customerState.customer}
        summaries={customerSummaries}
        wallets={walletsState.wallets}
        xrates={xratesState.xrates}
        isLoading={customerSummariesState.pending || xratesState.pending}
      />
      <WalletsTable
        wallets={walletsState.wallets}
        setSelectedWallet={setSelectedWallet}
        setModalMode={setModalMode}
        isLoading={walletsState.pending}
      />
      <TransfersTable transfers={mappedTransfers} />
      <AdjustmentsTable
        customer={customerState.customer}
        summaries={customerSummaries}
        allSummaries={customerSummariesState.summaries}
        xrates={xratesState.xrates}
        isLoading={customerSummariesState.pending}
        error={customerSummariesState.error}
      />
      <AccountsTable
        accounts={accountsState.accounts}
        bets={accountBets}
        isLoading={accountsState.pending}
        error={accountsState.error}
      />
      <XactAccountsTable />
      <ThemedModal open={modalMode != null}>
        {modalMode === 'credit' && (
          <FadeIn>
            <CreditModal
              wallet={selectedWallet}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'threshold' && (
          <FadeIn>
            <ThresholdModal
              wallet={selectedWallet}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'minBalance' && (
          <FadeIn>
            <MinBalanceModal
              wallet={selectedWallet}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'adjustment' && (
          <FadeIn>
            <AdjustmentModal
              clientCode={clientCode}
              customerSummaries={customerSummariesState.summaries}
              providerSummaries={providerSummariesState.summaries}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
      </ThemedModal>
    </Container>
  );
};
